/** Logical game dimensions — always 960×576 regardless of display */
export const GAME_W = 960;
export const GAME_H = 576;

/**
 * Maximum allowed per-axis stretch ratio before we fall back to letterbox.
 * 1.4 covers everything from 4:3 (ratio ≈ 1.25 on a 5:3 game) up to 21:9
 * ultra-wide (ratio ≈ 1.40). Beyond that — typically portrait orientation
 * or extreme aspect ratios — UI becomes unreadable, so we letterbox instead
 * of distorting the game world.
 */
const MAX_AXIS_RATIO = 1.4;

/**
 * Canvas = CSS display size (zero CSS scaling → 1:1 pixel mapping).
 * Camera zoom maps game coordinates (960×576) to canvas pixels.
 * X and Y axes can scale independently — `setupCamera` applies both.
 */
let _zoomX = 1;
let _zoomY = 1;

export function getZoom(): number { return Math.max(_zoomX, _zoomY); }
export function getZoomX(): number { return _zoomX; }
export function getZoomY(): number { return _zoomY; }
export function getTextRes(): number { return getZoom(); }

/**
 * Compute layout metrics. The default path stretches the canvas to fill the
 * viewport so there are no black bars (separate X/Y zoom on the camera). On
 * extreme aspect ratios (portrait, ultra-ultra-wide) we fall back to a
 * uniform-zoom letterbox to keep UI readable.
 */
export function computeMetrics(containerW: number, containerH: number) {
  const zoomX = containerW / GAME_W;
  const zoomY = containerH / GAME_H;
  const axisRatio = Math.max(zoomX / zoomY, zoomY / zoomX);

  if (axisRatio <= MAX_AXIS_RATIO) {
    _zoomX = zoomX;
    _zoomY = zoomY;
    return {
      canvasW: containerW,
      canvasH: containerH,
      cssW: containerW,
      cssH: containerH,
      cssLeft: 0,
      cssTop: 0,
    };
  }

  // Letterbox fallback for extreme aspect ratios.
  const uniform = Math.min(zoomX, zoomY);
  _zoomX = uniform;
  _zoomY = uniform;
  const cssW = Math.round(GAME_W * uniform);
  const cssH = Math.round(GAME_H * uniform);
  return {
    canvasW: cssW,
    canvasH: cssH,
    cssW,
    cssH,
    cssLeft: Math.round((containerW - cssW) / 2),
    cssTop: Math.round((containerH - cssH) / 2),
  };
}

function camOffset(vpGameW: number, vpGameH: number): { x: number; y: number } {
  return {
    x: -(vpGameW * (_zoomX - 1)) / 2,
    y: -(vpGameH * (_zoomY - 1)) / 2,
  };
}

/**
 * Configure a scene's camera and auto-update on resize. Uses separate X/Y
 * zoom (Phaser 3.50+ supports the two-arg form of setZoom) so the game world
 * stretches to fill the canvas. Camera viewport rectangles are computed from
 * GAME_W/GAME_H × the matching axis zoom — when computeMetrics took the
 * letterbox fallback, _zoomX === _zoomY and this collapses to the legacy
 * uniform-scale identity.
 */
export function setupCamera(
  scene: Phaser.Scene,
  cam: Phaser.Cameras.Scene2D.Camera,
  mode: 'full' | 'overlay',
): void {
  const apply = () => {
    cam.setZoom(_zoomX, _zoomY);
    if (mode === 'overlay') {
      cam.setViewport(
        Math.round((GAME_W / 2) * _zoomX), 0,
        Math.round(480 * _zoomX),
        Math.round(GAME_H * _zoomY),
      );
      const off = camOffset(480, GAME_H);
      cam.scrollX = off.x;
      cam.scrollY = off.y;
    } else {
      cam.setViewport(
        0, 0,
        Math.round(GAME_W * _zoomX),
        Math.round(GAME_H * _zoomY),
      );
      const off = camOffset(GAME_W, GAME_H);
      cam.scrollX = off.x;
      cam.scrollY = off.y;
    }
  };
  apply();
  scene.scale.on('resize', apply);
  scene.events.once('shutdown', () => scene.scale.off('resize', apply));
}
